import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ErrorBoundary from './components/ErrorBoundary';
import FullScreenLoader from './components/FullScreenLoader';

/**
 * Route boundary: allow full fidelity only for known static segments; collapse likely IDs.
 */
const ROUTE_ID_PATTERN = /^(?:\d+|[0-9a-fA-F]{6,}|[0-9a-fA-F-]{8,})$/;

export function sanitizeRoutePath(pathname: string): string {
  const cleanPath = pathname.split('?')[0].split('#')[0];
  const segments = cleanPath.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  const sanitizedSegments = segments.map((segment, index) => {
    if (index === 0) {
      return segment;
    }

    if (ROUTE_ID_PATTERN.test(segment)) {
      return ':id';
    }

    return segment;
  });

  return `/${sanitizedSegments.join('/')}`;
}

const RoutePerformanceTracker: React.FC = () => {
  const location = useLocation();
  const previousPathRef = useRef<string>('/');

  useEffect(() => {
    const currentPath = sanitizeRoutePath(location.pathname);
    const previousPath = sanitizeRoutePath(previousPathRef.current);

    if (previousPathRef.current && previousPath !== currentPath) {
      Sentry.startSpan({ name: `Route change: ${currentPath}`, op: 'navigation' }, (span) => {
        span.setAttribute('from', previousPath);
        span.setAttribute('to', currentPath);
      });
    }

    if (import.meta.env.DEV) {
      console.log(`[Route] ${previousPath || 'initial'} -> ${currentPath}`);
    }

    previousPathRef.current = currentPath;
  }, [location]);

  return null;
};

const ProtectedLayout = lazy(() => import('./components/ProtectedLayout'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ReportDetailsPage = lazy(() => import('./pages/ReportDetailsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const RetentionGovernancePage = lazy(() => import('./pages/RetentionGovernancePage'));
const UserAccountsPage = lazy(() => import('./pages/UserAccountsPage'));

const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <RoutePerformanceTracker />
      <ErrorBoundary>
        <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<ProtectedLayout />}>
                <Route path="/reports">
                  <Route index element={<ReportsPage />} />
                  <Route path=":id" element={<ReportDetailsPage />} />
                </Route>
                <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']} />}>
                  <Route index element={<UserAccountsPage />} />
                </Route>
                <Route
                  path="/retention-governance"
                  element={<ProtectedRoute allowedRoles={['admin']} />}
                >
                  <Route index element={<RetentionGovernancePage />} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<LoginPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRouter;
